import { isRequestError } from 'utils/requestError';
import { InputSelect, Loading } from 'components';
import { PopupWraper } from 'components/popup';
import { numberFormat } from 'configs';
import { Button, useForm } from 'modules';
import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  useGetFinancialLimitOptionsQuery,
  useGetUserInfoQuery,
  useUpdateUserFinancialLimitsMutation,
} from 'services';
import type { UpdateUserFinancialLimitDto, UserEntity } from 'services/openapi';
import { normalizedPhoneNumber } from 'utils/helpers/helpers';

type UpdateFinancialLimitsProps = {
  userId: string;
  onClose: () => void;
  forceUpdateTable: () => void;
};

type FinancialLimitField =
  | 'perTransactionLimit'
  | 'dailyLimit'
  | 'monthlyLimit';

const limitLabels: Record<FinancialLimitField, string> = {
  perTransactionLimit: 'Giới hạn mỗi giao dịch',
  dailyLimit: 'Giới hạn trong ngày',
  monthlyLimit: 'Giới hạn trong tháng',
};

const limitFields: FinancialLimitField[] = [
  'perTransactionLimit',
  'dailyLimit',
  'monthlyLimit',
];

const formatLimit = (value?: number) =>
  typeof value === 'number' ? `${numberFormat.format(value)} VNĐ` : '-';

const toLimitOptions = (values?: number[]) =>
  (values || []).map((value) => ({
    label: formatLimit(value),
    value,
  }));

const formContentStyle: React.CSSProperties = {
  padding: '8px 12px 4px',
};

const userSummaryStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '10px 0 16px',
  borderBottom: '1px solid var(--border)',
  marginBottom: 4,
};

const userNameStyle: React.CSSProperties = {
  color: 'var(--text-primary)',
  fontSize: 15,
  fontWeight: 700,
  lineHeight: 1.35,
};

const userMetaStyle: React.CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 12,
  lineHeight: 1.45,
  marginTop: 2,
};

const userStatusStyle: React.CSSProperties = {
  flex: '0 0 auto',
  borderRadius: 999,
  padding: '5px 10px',
  fontSize: 12,
  fontWeight: 700,
  background: 'rgba(34, 197, 94, 0.16)',
  color: '#22c55e',
};

const userStatusInactiveStyle: React.CSSProperties = {
  ...userStatusStyle,
  background: 'rgba(239, 68, 68, 0.14)',
  color: '#ef4444',
};

const limitListStyle: React.CSSProperties = {
  display: 'grid',
  gap: 0,
};

const limitFieldStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1fr) minmax(220px, 1.2fr)',
  gap: 16,
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: '1px solid var(--border)',
};

const limitLabelStyle: React.CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'uppercase',
};

const actionStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  justifyContent: 'flex-end',
  marginTop: 20,
};

const UpdateFinancialLimits = ({
  userId,
  onClose,
  forceUpdateTable,
}: UpdateFinancialLimitsProps) => {
  const {
    data: userInfo,
    isFetching: isFetchingUserInfo,
    refetch: refetchUserInfo,
  } = useGetUserInfoQuery({ userId });
  const { data: dataOptions, isLoading: isLoadingOptions } =
    useGetFinancialLimitOptionsQuery();
  const { mutateAsync, isLoading: isUpdating } =
    useUpdateUserFinancialLimitsMutation();

  const dataForm = userInfo?.data as UserEntity | undefined;
  const options = dataOptions?.data;

  const perTransactionLimitOptions = useMemo(
    () => toLimitOptions(options?.perTransactionLimits),
    [options?.perTransactionLimits],
  );
  const dailyLimitOptions = useMemo(
    () => toLimitOptions(options?.dailyLimits),
    [options?.dailyLimits],
  );
  const monthlyLimitOptions = useMemo(
    () => toLimitOptions(options?.monthlyLimits),
    [options?.monthlyLimits],
  );

  const optionsByField: Record<FinancialLimitField, any[]> = {
    perTransactionLimit: perTransactionLimitOptions,
    dailyLimit: dailyLimitOptions,
    monthlyLimit: monthlyLimitOptions,
  };

  const { handleSubmit, getInputProps } = useForm({
    enableReinitialize: true,
    structure: [
      {
        name: 'perTransactionLimit',
        defaultValue: dataForm?.perTransactionLimit || '',
      },
      {
        name: 'dailyLimit',
        defaultValue: dataForm?.dailyLimit || '',
      },
      {
        name: 'monthlyLimit',
        defaultValue: dataForm?.monthlyLimit || '',
      },
    ],
    onSubmit: async (values) => {
      const requestBody = {
        perTransactionLimit: values.perTransactionLimit,
        dailyLimit: values.dailyLimit,
        monthlyLimit: values.monthlyLimit,
      };

      if (
        !requestBody.perTransactionLimit ||
        !requestBody.dailyLimit ||
        !requestBody.monthlyLimit
      ) {
        toast.error('Vui lòng chọn đầy đủ hạn mức');
        return;
      }

      try {
        await mutateAsync({
          userId,
          requestBody: requestBody as UpdateUserFinancialLimitDto,
        });
        await refetchUserInfo();
        forceUpdateTable();
        toast.success('Cập nhật hạn mức thành công');
        onClose();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error?.message || 'Cập nhật hạn mức thất bại');
        }
      }
    },
  });

  return (
    <PopupWraper
      center
      title='Quản lý hạn mức người dùng'
      onClose={onClose}
      style={{ maxWidth: 560 }}
    >
      <Loading
        isLoading={(isFetchingUserInfo && !userInfo) || isLoadingOptions}
      >
        <div className='container' style={formContentStyle}>
          <div style={userSummaryStyle}>
            <div>
              <div style={userNameStyle}>{dataForm?.fullName || '-'}</div>
              <div style={userMetaStyle}>
                {dataForm?.phoneNumber
                  ? normalizedPhoneNumber(dataForm.phoneNumber)
                  : '-'}
                {dataForm?.compName ? ` · ${dataForm.compName}` : ''}
              </div>
            </div>
            <div
              style={
                dataForm?.isActive ? userStatusStyle : userStatusInactiveStyle
              }
            >
              {dataForm?.isActive ? 'Hoạt động' : 'Ngưng hoạt động'}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={limitListStyle}>
              {limitFields.map((field, index) => (
                <div
                  key={field}
                  style={{
                    ...limitFieldStyle,
                    ...(index === limitFields.length - 1
                      ? { borderBottom: 0 }
                      : {}),
                  }}
                >
                  <div style={limitLabelStyle}>{limitLabels[field]}</div>
                  <div>
                    <InputSelect
                      {...getInputProps(field)}
                      options={optionsByField[field]}
                      isClear={false}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div style={actionStyle}>
              <Button label='Hủy' buttonType='grey' onClick={onClose} />
              <Button
                label='Cập nhật'
                buttonType='success'
                type='submit'
                isLoading={isUpdating}
              />
            </div>
          </form>
        </div>
      </Loading>
    </PopupWraper>
  );
};

export default UpdateFinancialLimits;
